import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "wouter";
import { ChevronDown, ChevronUp } from "lucide-react";
import { PerformanceCagrTooltip } from "./PerformanceCagrTooltip";

import { valueFormat } from "../../../../common/src/utils/FormatFunctions";
import { usePrevious } from "../../../../components/src/common/Utils";
import { KeyPerformanceIndicators, Language } from "../../../../components/src";
import { PerformanceTimeSeriesChart } from "../../components/PerformanceTimeSeriesChart";
import { useQueryState } from "../../../../components/src/use-query-state";
import { PerformancePeriodSelector } from "./PerformancePeriodSelector";
import { PerformanceKpiStrip } from "./PerformanceKpiStrip";
import { TableGrouper } from "../../../../components/src/react-table/TableGrouper";
import { firstChildAggregation } from "../../../../components/src/react-table/features";
import { constructAggregationFn } from "@tanstack/react-table";
import { usePerformance } from "./performanceGraphHooks";

import {
    getRowData,
    showModelIndex,
    aggregate2DArrayOfNumbers,
    isValidRow,
    accountReturn,
    benchmarkReturn,
    diffReturn,
    percentageFormat,
    yearFracActual365
} from "./functions";
import { Party } from "../../types.generated";
import { SelectedRowType, PerformanceOverviewProps, ProcessedData } from "./interfaces";
import { useProcessedData } from "./hooks";

const ReturnBadge = ({ value }: { value: number | null }): React.ReactElement | null => {
    if (value === null || value === undefined || Number.isNaN(value)) return null;
    const positive = value >= 0;
    const cls = positive
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-rose-50 text-rose-700 border-rose-200";
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-base font-medium tabular-nums border ${cls}`}
        >
            {positive ? "+" : ""}
            {percentageFormat(value)}
        </span>
    );
};

interface SparklineProps {
    series: number[];
    dateSeries: string[];
    startDate: string;
    endDate: string;
}

const Sparkline = ({ series, dateSeries, startDate, endDate }: SparklineProps): React.ReactElement | null => {
    if (!series || series.length < 2 || !dateSeries || dateSeries.length !== series.length) return null;
    let startIdx = dateSeries.findIndex((d) => d >= startDate);
    if (startIdx === -1) startIdx = 0;
    let endIdx = dateSeries.findIndex((d) => d === endDate);
    if (endIdx === -1) endIdx = dateSeries.length - 1;
    if (endIdx <= startIdx) return null;

    const slice = series.slice(startIdx, endIdx + 1);
    let min = Infinity;
    let max = -Infinity;
    for (const v of slice) {
        if (Number.isFinite(v)) {
            if (v < min) min = v;
            if (v > max) max = v;
        }
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    const range = max - min || 1;

    const w = 80;
    const h = 22;
    const step = w / (slice.length - 1);
    const points = slice
        .map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`)
        .join(" ");

    const positive = slice[slice.length - 1] >= slice[0];
    const stroke = positive ? "#059669" : "#e11d48";

    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
            <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
};

const PerformanceCard = ({ selectedRow }: { selectedRow: SelectedRowType }): React.ReactElement => {
    const { alignedTimeSeries } = usePerformance(selectedRow);
    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-slate-900 font-semibold m-0">Performance</h4>
                <span className="text-xs text-slate-500 tabular-nums">
                    {selectedRow.startDate} → {selectedRow.endDate}
                </span>
            </div>
            <PerformanceTimeSeriesChart timeseries={alignedTimeSeries} style={{ height: "50vh" }} />
        </div>
    );
};

const KpiCardBlock = ({ selectedRow }: { selectedRow: SelectedRowType }): React.ReactElement => {
    const { alignedTimeSeries, value } = usePerformance(selectedRow);
    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 h-full">
            <h4 className="text-slate-900 font-semibold m-0 mb-2">Key Performance Indicators (%)</h4>
            <KeyPerformanceIndicators
                masterTimeSeriesName={selectedRow.name}
                timeseries={alignedTimeSeries}
                navValue={value}
                riskFreeTimeSeries={selectedRow.riskFree}
                locale={Language.En}
            />
        </div>
    );
};

interface InteractiveLabelProps {
    label: string;
    isSelected: boolean;
    onSelect: () => void;
}

const InteractiveLabel = ({ label, isSelected, onSelect }: InteractiveLabelProps): React.ReactElement => {
    const base =
        "inline-flex items-center px-2 py-0.5 rounded cursor-pointer transition-colors duration-100 hover:bg-sky-50 hover:text-sky-700";
    const selected = isSelected ? " bg-sky-100 text-sky-800 font-semibold ring-1 ring-sky-300" : " text-gray-800";
    return (
        <span
            className={base + selected}
            role="button"
            tabIndex={0}
            onClick={(e) => {
                e.stopPropagation();
                onSelect();
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect();
                }
            }}
        >
            {label}
        </span>
    );
};

export const PerformanceOverview = ({ client, data, endDate, setEndDate: _setEndDate }: PerformanceOverviewProps): React.ReactElement => {
    const [startDate] = useQueryState("startDate", client.firstTradeDate);
    const [, setQueryArgs] = useSearchParams();
    const [errors] = useState<string[]>([]);

    const [selectedRow, setSelectedRow] = useState<SelectedRowType>({
        name: "",
        performance: null,
        values: [],
        instrumentPerformances: [],
        benchmarks: [],
        riskFree: null,
        startDate: startDate,
        endDate: endDate,
        dateSeries: [],
        positionFlag: []
    });

    const [hideZerosChecked, setHideZerosChecked] = useState(true);
    const [expandPositionsChecked, setExpandPositionsChecked] = useState(false);
    const [treeOpen, setTreeOpen] = useState(true);

    // The performance chart is expensive to mount and stalls the first paint by several seconds
    // when switching to this tab. Defer it one tick so the KPI strip and portfolio table render
    // immediately; the chart then mounts right after.
    const [showHeavyContent, setShowHeavyContent] = useState(false);
    useEffect(() => {
        const id = setTimeout(() => setShowHeavyContent(true), 0);
        return () => clearTimeout(id);
    }, []);

    const previousSelectedRow: SelectedRowType = usePrevious(selectedRow);
    const previousSelectedClient: Partial<Party> = usePrevious(client);

    // Signature of the payload the current `selectedRow` snapshot was built from. Rebuilding is keyed on
    // this value (not on the urql result's object identity) so a `cache-and-network` refetch that hands
    // back a value-identical payload with a fresh reference does NOT re-run setSelectedRow + setQueryArgs.
    // The old `data !== previousData` identity check did, and combined with the setQueryArgs navigation it
    // produced an update loop that exceeded React's update-depth limit (React error #185).
    const builtFromSignatureRef = React.useRef<string | null>(null);

    const sortBy = React.useMemo(
        () => [
            { id: "level1", desc: false },
            { id: "level2", desc: false },
            { id: "level3", desc: false },
            { id: "level4", desc: false },
            { id: "instrumentName", desc: false }
        ],
        []
    );

    const processedData: ProcessedData = useProcessedData({ endDate, startDate, data });

    const columns = React.useMemo(() => {
        if (!processedData) return [];
        const { dataWithLevels, dateSeries, benchmarksById, accountSeriesById, accountPositionsById, modelSeriesById, firstSelectedRow } =
            processedData;
        // Total portfolio value as the sum of the most recent value across all leaf instrument rows.
        // Used by the "Weight" column to express each row's share of the portfolio.
        const totalPortfolioValue = dataWithLevels.reduce((acc: number, row: { values: number[] }) => {
            const last = row.values && row.values.length ? row.values[row.values.length - 1] : 0;
            return acc + (typeof last === "number" && !Number.isNaN(last) ? last : 0);
        }, 0);
        // The period-return columns annualize (CAGR) only when the selected range is longer than one
        // year; for one year and under the raw (direct) return is shown. The group header reflects which
        // of the two the user is currently looking at.
        const periodIsAnnualized = yearFracActual365(new Date(startDate), new Date(endDate)) > 1;
        return [
            {
                Header: "",
                id: "metaData",
                columns: [
                    {
                        id: "rootId",
                        header: () => {
                            if (dataWithLevels && dataWithLevels.length > 0) {
                                return <Link to={`/parties/${client._id}/accounts/${dataWithLevels[0].accountId}`}>Client</Link>;
                            } else {
                                return <Link to={`/parties/${client._id}/accounts`}>Client</Link>;
                            }
                        },
                        accessorKey: "rootId",
                        size: 500,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const isSelected = selectedRow.name === row.original.root;
                            return (
                                <InteractiveLabel
                                    label={row.original.root}
                                    isSelected={isSelected}
                                    onSelect={() => setSelectedRow(firstSelectedRow)}
                                />
                            );
                        },
                        getIsGrouped: () => true
                    },
                    {
                        header: "Client Description",
                        accessorKey: "root",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        id: "level1Id",
                        header: () => {
                            if (dataWithLevels && dataWithLevels.length > 1) {
                                return <Link to={`/parties/${client._id}/accounts/${dataWithLevels[1].accountId}`}>Account L1</Link>;
                            } else {
                                return <Link to={`/parties/${client._id}/accounts`}>Account L1</Link>;
                            }
                        },
                        accessorKey: "level1Id",
                        cell: (cellProps) => {
                            const { row, cell } = cellProps;
                            const label = row.original.level1;
                            const isSelected = selectedRow.name === label;
                            return (
                                <InteractiveLabel
                                    label={label}
                                    isSelected={isSelected}
                                    onSelect={() => {
                                        const value = cell.getValue();
                                        const account = processedData.accountsById[value];
                                        const next = getRowData(
                                            row,
                                            processedData,
                                            label,
                                            startDate,
                                            endDate,
                                            showModelIndex(account, processedData.maxAccountLevels, 1)
                                        );
                                        setSelectedRow(next);
                                    }}
                                />
                            );
                        }
                    },
                    {
                        id: "level2Id",
                        header: () => {
                            if (dataWithLevels && dataWithLevels.length > 2) {
                                return <Link to={`/parties/${client._id}/accounts/${dataWithLevels[2].accountId}`}>Account L2</Link>;
                            } else {
                                return <Link to={`/parties/${client._id}/accounts`}>Account L2</Link>;
                            }
                        },
                        accessorKey: "level2Id",
                        cell: (cellProps) => {
                            const { row, cell } = cellProps;
                            const label = row.original.level2;
                            const isSelected = selectedRow.name === label;
                            return (
                                <InteractiveLabel
                                    label={label}
                                    isSelected={isSelected}
                                    onSelect={() => {
                                        const value = cell.getValue();
                                        const account = processedData.accountsById[value];
                                        const next = getRowData(
                                            row,
                                            processedData,
                                            label,
                                            startDate,
                                            endDate,
                                            showModelIndex(account, processedData.maxAccountLevels, 2)
                                        );
                                        setSelectedRow(next);
                                    }}
                                />
                            );
                        }
                    },
                    {
                        id: "level3Id",
                        header: () => {
                            if (dataWithLevels && dataWithLevels.length > 3) {
                                return <Link to={`/parties/${client._id}/accounts/${dataWithLevels[3].accountId}`}>Account L3</Link>;
                            } else {
                                return <Link to={`/parties/${client._id}/accounts`}>Account L3</Link>;
                            }
                        },
                        accessorKey: "level3Id",
                        cell: (cellProps) => {
                            const { row, cell } = cellProps;
                            const label = row.original.level3;
                            const isSelected = selectedRow.name === label;
                            return (
                                <InteractiveLabel
                                    label={label}
                                    isSelected={isSelected}
                                    onSelect={() => {
                                        const value = cell.getValue();
                                        const account = processedData.accountsById[value];
                                        const next = getRowData(
                                            row,
                                            processedData,
                                            label,
                                            startDate,
                                            endDate,
                                            showModelIndex(account, processedData.maxAccountLevels, 3)
                                        );
                                        setSelectedRow(next);
                                    }}
                                />
                            );
                        }
                    },
                    {
                        id: "level4Id",
                        header: () => {
                            if (dataWithLevels && dataWithLevels.length > 4) {
                                return <Link to={`/parties/${client._id}/accounts/${dataWithLevels[4].accountId}`}>Account L4</Link>;
                            } else {
                                return <Link to={`/parties/${client._id}/accounts`}>Account L4</Link>;
                            }
                        },
                        accessorKey: "level4Id",
                        cell: (cellProps) => {
                            const { row, cell } = cellProps;
                            const label = row.original.level4;
                            const isSelected = selectedRow.name === label;
                            return (
                                <InteractiveLabel
                                    label={label}
                                    isSelected={isSelected}
                                    onSelect={() => {
                                        const value = cell.getValue();
                                        const account = processedData.accountsById[value];
                                        const next = getRowData(
                                            row,
                                            processedData,
                                            label,
                                            startDate,
                                            endDate,
                                            showModelIndex(account, processedData.maxAccountLevels, 4)
                                        );
                                        setSelectedRow(next);
                                    }}
                                />
                            );
                        }
                    },
                    {
                        header: "Account L1 Description",
                        accessorKey: "level1",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        header: "Account L2 Description",
                        accessorKey: "level2",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        header: "Account L3 Description",
                        accessorKey: "level3",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        header: "Account L4 Description",
                        accessorKey: "level4",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        id: "instrumentName",
                        accessorFn: (row) => (row.instrument.longName ? row.instrument.longName : row.instrument.name),
                        header: "Instrument",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const benchmark = row.getIsGrouped()
                                ? benchmarksById[row.groupingValue]
                                : benchmarksById[row.original.instrumentId]
                                  ? benchmarksById[row.original.instrumentId]
                                  : benchmarksById[row.original.accountId];
                            const label = cellProps.getValue();
                            const isSelected = selectedRow.name === label;
                            return (
                                <span className="inline-flex items-center gap-1">
                                    {label ? (
                                        <Link className="text-sky-600 hover:text-sky-800" to={`/instruments/${row.original.instrumentId}`}>
                                            #
                                        </Link>
                                    ) : null}
                                    {label ? (
                                        <InteractiveLabel
                                            label={label}
                                            isSelected={isSelected}
                                            onSelect={() => {
                                                const next = getRowData(
                                                    row,
                                                    processedData,
                                                    label,
                                                    startDate,
                                                    endDate,
                                                    false,
                                                    benchmark
                                                );
                                                setSelectedRow(next);
                                            }}
                                        />
                                    ) : null}
                                </span>
                            );
                        }
                    },

                    {
                        header: "First trade date",
                        accessorKey: "firstTradeDate",
                        aggregationFn: constructAggregationFn({
                            aggregate: ({ rows }) => {
                                let minDateString = "2999-12-30";
                                for (const row of rows) {
                                    if (row.original.firstTradeDate < minDateString) {
                                        minDateString = row.original.firstTradeDate;
                                    }
                                }
                                return minDateString;
                            }
                        }),
                        getCanGroup: () => false
                    },
                    {
                        header: "Value (SEK)",
                        id: "valueSEK",
                        accessorFn: (row) => {
                            return row.values[row.values.length - 1];
                        },
                        aggregationFn: "sum",
                        cell: (cellProps) => <div style={{ textAlign: "left" }}>{valueFormat(cellProps.getValue())}</div>,
                        aggregatedCell: null,
                        getCanGroup: () => false
                    },
                    {
                        header: "Weight",
                        id: "weight",
                        accessorFn: (row) => {
                            return row.values && row.values.length ? row.values[row.values.length - 1] : 0;
                        },
                        aggregationFn: "sum",
                        cell: (cellProps) => {
                            const value = cellProps.getValue() as number;
                            if (totalPortfolioValue === 0 || typeof value !== "number" || Number.isNaN(value)) {
                                return <div style={{ textAlign: "left" }}>—</div>;
                            }
                            return <div style={{ textAlign: "left" }}>{percentageFormat(value / totalPortfolioValue)}</div>;
                        },
                        aggregatedCell: null,
                        getCanGroup: () => false
                    },
                    {
                        header: "Values",
                        accessorKey: "values",
                        aggregationFn: constructAggregationFn({
                            aggregate: ({ rows, getValue }) => aggregate2DArrayOfNumbers(rows.map((row) => getValue(row) as number[]))
                        }),
                        cell: (_cellProps) => {
                            return null;
                        }
                    },
                    {
                        header: "Series",
                        accessorKey: "series",
                        cell: (_cellProps) => {
                            return null;
                        },
                        getCanGroup: () => false
                    }
                ]
            },
            {
                header: (
                    <div
                        className="w-full text-center text-xs font-medium text-slate-500"
                        data-tooltip-id="ct-tooltip"
                        data-tooltip-place="top"
                        data-tooltip-position-strategy="fixed"
                        data-tooltip-content={
                            "Compound Annual Growth Rate (CAGR) is used for periods over one year. Simple return is shown for one year and under."
                        }
                    >
                        {periodIsAnnualized ? "Returns are shown as annualized" : "Returns are shown as simple"}
                    </div>
                ),
                id: "returnPer",
                columns: [
                    {
                        header: "Account",
                        id: "returnPerAccount",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            if (!row.getIsGrouped() && row.original.isPartyInstrument)
                                return <span className="text-base text-slate-400">N.A.</span>;
                            const lastDate = new Date(endDate);
                            const firstDate = new Date(startDate);
                            return <ReturnBadge value={accountReturn(row, dateSeries, firstDate, lastDate, accountSeriesById, true)} />;
                        },
                        aggregatedCell: null
                    },
                    {
                        header: "Benchmark",
                        id: "returnPerBenchmark",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            if (!row.getIsGrouped() && row.original.isPartyInstrument)
                                return <span className="text-base text-slate-400">N.A.</span>;
                            const lastDate = new Date(endDate);
                            const firstDate = new Date(startDate);
                            return (
                                <ReturnBadge
                                    value={benchmarkReturn(
                                        row,
                                        dateSeries,
                                        firstDate,
                                        lastDate,
                                        benchmarksById,
                                        accountPositionsById,
                                        modelSeriesById,
                                        true,
                                        true
                                    )}
                                />
                            );
                        },
                        aggregatedCell: null
                    },
                    {
                        header: "Difference",
                        id: "returnPerDifference",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            if (!row.getIsGrouped() && row.original.isPartyInstrument)
                                return <span className="text-base text-slate-400">N.A.</span>;
                            const lastDate = new Date(endDate);
                            const firstDate = new Date(startDate);
                            return (
                                <ReturnBadge
                                    value={diffReturn(
                                        row,
                                        dateSeries,
                                        firstDate,
                                        lastDate,
                                        benchmarksById,
                                        accountSeriesById,
                                        accountPositionsById,
                                        modelSeriesById,
                                        true,
                                        true
                                    )}
                                />
                            );
                        },
                        aggregatedCell: null
                    },
                    {
                        header: "Trend",
                        id: "sparkline",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            if (!row.getIsGrouped() && row.original.isPartyInstrument) return null;
                            const series = row.getIsGrouped()
                                ? accountSeriesById[row.groupingValue as string]
                                : (row.original.series as number[]);
                            if (!series) return null;
                            return <Sparkline series={series} dateSeries={dateSeries} startDate={startDate} endDate={endDate} />;
                        },
                        aggregatedCell: null
                    }
                ]
            }
        ];
    }, [client._id, startDate, endDate, processedData, selectedRow.name]);

    useEffect(() => {
        if (processedData) {
            const { firstSelectedRow, dateSeries } = processedData;

            const startDateAttribution = !previousSelectedClient
                ? dateSeries[0]
                : previousSelectedClient && previousSelectedClient.firstTradeDate === startDate
                  ? dateSeries[0]
                  : startDate > dateSeries[0] || startDate < dateSeries[dateSeries.length - 1]
                    ? startDate
                    : dateSeries[0];

            // `selectedRow` holds deep snapshots of series/values taken from the payload it was built
            // from, so it must be rebuilt whenever a genuinely new performance payload arrives — both on
            // client switches and when a refetch delivers updated numbers for the same client. We detect
            // that with a value-based signature rather than the urql result's object identity, so
            // value-identical re-emits (which get a fresh reference under `cache-and-network`) do not
            // trigger a rebuild/navigation and cannot feed an update loop.
            const dates: string[] = (data && data.performance && data.performance.dates) || [];
            const values: number[] = (data && data.performance && data.performance.values) || [];
            const payloadSignature = [
                data && data.client ? data.client._id : "",
                endDate,
                dates.length,
                dates.length ? dates[dates.length - 1] : "",
                values.length ? values[values.length - 1] : ""
            ].join("|");
            const isNewPayload = builtFromSignatureRef.current !== payloadSignature;

            if (selectedRow.name === "" || isNewPayload) {
                builtFromSignatureRef.current = payloadSignature;
                setSelectedRow(firstSelectedRow);
                if (startDateAttribution !== startDate) {
                    setQueryArgs({ startDate: startDateAttribution, endDate });
                }
            } else if (previousSelectedRow && selectedRow.name !== previousSelectedRow.name) {
                if (startDateAttribution !== startDate) {
                    setQueryArgs({ startDate: startDateAttribution, endDate });
                }
            }
        }
    }, [client._id, data, endDate, previousSelectedClient, previousSelectedRow, processedData, selectedRow, setQueryArgs, startDate]);

    if (errors && errors.length > 0) return <p>{errors}</p>;
    if (!processedData) return <div></div>;

    if (!client.firstTradeDate) return <p>Missing firstTradeDate</p>;

    if (startDate && endDate && endDate < startDate)
        return (
            <p>
                EndDate {endDate} cannot be earlier than startDate {startDate}
            </p>
        );

    let treeRootExist = false;
    if (data && "client" in data && "accounts" in data.client && data.client.topAccount) {
        treeRootExist = true;
    }

    if (!treeRootExist) return <p>No account tree found</p>;
    const { maxAccountLevels, dataWithLevels, dateSeries } = processedData;

    const expandedPositions = expandPositionsChecked ? true : {};

    const hiddenColumns = ["series", "values", "root", "level1", "level2", "level3", "level4", "positionFlag", "firstTradeDate"];

    const groupBy = ["rootId", "level1Id", "level2Id", "level3Id", "level4Id"];
    if (maxAccountLevels && maxAccountLevels.length < 5) {
        delete groupBy[4];
        hiddenColumns.push("level4Id");
        if (maxAccountLevels && maxAccountLevels.length < 4) {
            delete groupBy[3];
            hiddenColumns.push("level3Id");
            if (maxAccountLevels && maxAccountLevels.length < 3) {
                delete groupBy[2];
                hiddenColumns.push("level2Id");
                if (maxAccountLevels.length < 2) {
                    delete groupBy[1];
                    hiddenColumns.push("level1Id");
                }
            }
        }
    }

    const rowInPosition = (startDateLocal: string, dateSeriesLocal: string[], positionFlag: number[]): boolean => {
        for (let i = 0; i < dateSeriesLocal.length; i++) {
            if (dateSeriesLocal[i] < startDateLocal) continue;
            if (positionFlag[i] === 1) return true;
        }
        return false;
    };

    const filteredDataWithLevels = hideZerosChecked
        ? dataWithLevels.filter((row) => rowInPosition(startDate, dateSeries, row.positionFlag))
        : dataWithLevels;

    const handleHideZerosChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setHideZerosChecked(e.target.checked);
    };

    const handleExpandPositionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setExpandPositionsChecked(e.target.checked);
    };

    const selectedRowWithDates: SelectedRowType = { ...selectedRow, endDate, startDate };

    return (
        <div className="space-y-4">
            <PerformanceCagrTooltip />
            <div className="sticky top-0 z-20 -mx-4 px-4 py-3 mb-1 bg-gray-100/90 backdrop-blur border-b border-slate-200 print-none">
                <div className="flex flex-wrap items-center gap-3">
                    <PerformancePeriodSelector firstAvailableStartDate={client.firstTradeDate} endDate={endDate} />
                    <label className="inline-flex items-center gap-2 select-none text-sm text-slate-700">
                        <input
                            type="checkbox"
                            checked={hideZerosChecked}
                            onChange={handleHideZerosChange}
                            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        Hide zeros
                    </label>
                    <label className="inline-flex items-center gap-2 select-none text-sm text-slate-700">
                        <input
                            type="checkbox"
                            checked={expandPositionsChecked}
                            onChange={handleExpandPositionsChange}
                            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        Expand positions
                    </label>
                </div>
            </div>

            {isValidRow(selectedRow) ? <PerformanceKpiStrip selectedRow={selectedRowWithDates} /> : null}

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <button
                    type="button"
                    onClick={() => setTreeOpen((open) => !open)}
                    className="w-full flex items-center justify-between p-4 border-b border-slate-200 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 rounded-t-xl"
                >
                    <div className="flex items-baseline gap-3">
                        <h4 className="text-base text-slate-900 font-semibold m-0">Portfolio</h4>
                        <span className="text-xs text-slate-500">{filteredDataWithLevels.length} rows</span>
                    </div>
                    {treeOpen ? (
                        <ChevronUp size={18} className="text-slate-500" aria-hidden />
                    ) : (
                        <ChevronDown size={18} className="text-slate-500" aria-hidden />
                    )}
                </button>
                {treeOpen ? (
                    <div className="p-2 text-base [&_table.table-xs_td]:!text-base [&_table.table-xs_th]:!text-base [&_table.table-xs_td_a]:!text-base [&_table.table-xs_th_a]:!text-base">
                        <TableGrouper
                            // Key intentionally excludes `endDate`, `startDate` and the filtered row length so
                            // that changing the date range or toggling "Hide zeros" does NOT remount the table
                            // and therefore preserves the user's expanded/collapsed tree state.
                            // `expandPositionsChecked` is included so toggling "Expand positions" remounts with
                            // the correct initial expanded state (TableGrouper only reads `expanded` on mount).
                            key={`${client._id}-${expandPositionsChecked}-${maxAccountLevels}-${groupBy.join()}`}
                            columns={columns}
                            data={filteredDataWithLevels}
                            expanded={expandedPositions}
                            groupBy={groupBy}
                            hiddenColumns={hiddenColumns}
                            compactMode={true}
                            captionColumn="instrumentName"
                            sortBy={sortBy}
                            countLeafDescendants={true}
                        />
                    </div>
                ) : null}
            </div>

            {showHeavyContent && isValidRow(selectedRow) ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-2">
                        <PerformanceCard selectedRow={selectedRowWithDates} />
                    </div>
                    <div className="lg:col-span-1">
                        <KpiCardBlock selectedRow={selectedRowWithDates} />
                    </div>
                </div>
            ) : null}
        </div>
    );
};
